/**
 * 轻量 Toast（替代原生 alert）
 *
 * 为什么不用 alert：原生弹窗是阻塞式的，必须手动点「确定」才能继续，
 * 保存/删除这类正向反馈不需要打断操作。Toast 2~3 秒自动消失。
 *
 * 用法：
 *   import toast from '../lib/toast';
 *   toast('已保存');            // 默认 info
 *   toast.success('保存成功');
 *   toast.error('保存失败：xxx');
 */

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

type Listener = (items: ToastItem[]) => void;

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<Listener>();

const emit = () => {
  const snapshot = [...items];
  listeners.forEach((listener) => listener(snapshot));
};

export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener([...items]);
  return () => {
    listeners.delete(listener);
  };
};

export const dismiss = (id: number) => {
  items = items.filter((item) => item.id !== id);
  emit();
};

const push = (message: string, type: ToastType, duration: number): number => {
  const id = ++seq;
  // 最多同时展示 5 条，超出丢最早的
  items = [...items.slice(-4), { id, message: String(message ?? ''), type }];
  emit();
  window.setTimeout(() => dismiss(id), duration);
  return id;
};

const toast: {
  (message: string, type?: ToastType, duration?: number): number;
  success: (message: string) => number;
  error: (message: string) => number;
  info: (message: string) => number;
  warning: (message: string) => number;
} = Object.assign(
  (message: string, type: ToastType = 'info', duration = 2600) => push(message, type, duration),
  {
    success: (message: string) => push(message, 'success', 2400),
    error: (message: string) => push(message, 'error', 3800),
    info: (message: string) => push(message, 'info', 2600),
    warning: (message: string) => push(message, 'warning', 3200),
  }
);

export default toast;
